import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Eye, 
  Edit, 
  Send,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  CreditCard,
  Link as LinkIcon
} from 'lucide-react';
import apiClient from '../api/config';
import { Invoice } from '../types/invoice';
import { useLanguage } from '../contexts/LanguageContext';
import toast from 'react-hot-toast';
import { handleApiError } from '../utils/errorHandler';
import PaymentModal from '../components/payments/PaymentModal';
import PaymentLinkGenerator from '../components/payments/PaymentLinkGenerator';
import SendInvoiceModal from '../components/invoice/SendInvoiceModal';
import ProcessInvoicePaymentModal from '../components/invoice/ProcessInvoicePaymentModal';
import CreateInvoiceModal from '../components/invoice/CreateInvoiceModal';

type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';

const InvoicesPage: React.FC = () => {
  const { t } = useLanguage();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | 'all'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showLinkGenerator, setShowLinkGenerator] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showProcessPaymentModal, setShowProcessPaymentModal] = useState(false);
  const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] = useState<Invoice | null>(null);
  const [selectedInvoiceForSending, setSelectedInvoiceForSending] = useState<Invoice | null>(null);
  const [selectedInvoiceForProcessing, setSelectedInvoiceForProcessing] = useState<Invoice | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchInvoices();
  }, [currentPage, statusFilter]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 20,
        status: statusFilter === 'all' ? undefined : statusFilter,
        search: searchTerm || undefined
      };
      
      const response = await apiClient.get('/invoices', { params });
      
      // Handle response based on backend format
      if (response.data.data) {
        setInvoices(response.data.data);
        setTotalPages(Math.ceil(response.data.total / 20) || 1);
      } else if (response.data.invoices) {
        setInvoices(response.data.invoices);
        setTotalPages(Math.ceil(response.data.total / 20) || 1);
      } else {
        setInvoices([]);
      }
    } catch (error) {
      const processedError = handleApiError(error);
      toast.error(processedError.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: InvoiceStatus) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'paid': return 'bg-green-100 text-green-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: InvoiceStatus) => {
    switch (status) {
      case 'draft': return <Edit className="w-4 h-4" />;
      case 'sent': return <Send className="w-4 h-4" />;
      case 'paid': return <CheckCircle className="w-4 h-4" />;
      case 'overdue': return <AlertTriangle className="w-4 h-4" />;
      case 'cancelled': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const formatCurrency = (amount: number, currency: string = 'KWD') => {
    return new Intl.NumberFormat('ar-KW', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ar-KW');
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchInvoices();
  };

  const handleDownloadPDF = async (invoiceId: number, invoiceNumber: string) => {
    try {
      const response = await apiClient.get(`/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_${invoiceNumber}.html`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      const processedError = handleApiError(error);
      toast.error(processedError.message);
    }
  };

  const handlePreviewInvoice = async (invoiceId: number) => {
    try {
      const response = await apiClient.get(`/invoices/${invoiceId}/preview`);
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write(response.data);
        newWindow.document.close();
      }
    } catch (error) {
      const processedError = handleApiError(error);
      toast.error(processedError.message);
    }
  };

  const handleExportAll = async () => {
    try {
      const response = await apiClient.get('/invoices/export', {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoices_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Invoices exported successfully');
    } catch (error) {
      const processedError = handleApiError(error);
      toast.error(processedError.message);
    }
  };

  const handleProcessPayment = (invoice: Invoice) => {
    setSelectedInvoiceForProcessing(invoice);
    setShowProcessPaymentModal(true);
  };

  const handleGeneratePaymentLink = (invoice: Invoice) => {
    setSelectedInvoiceForPayment(invoice);
    setShowLinkGenerator(true);
  };

  const handleSendInvoice = (invoice: Invoice) => {
    setSelectedInvoiceForSending(invoice);
    setShowSendModal(true);
  };

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
    setShowLinkGenerator(false);
    setSelectedInvoiceForPayment(null);
    fetchInvoices(); // Refresh to update payment status
  };

  const handleSendSuccess = () => {
    setShowSendModal(false);
    setSelectedInvoiceForSending(null);
    fetchInvoices(); // Refresh to update invoice status
  };

  const handleProcessPaymentSuccess = () => {
    setShowProcessPaymentModal(false);
    setSelectedInvoiceForProcessing(null);
    fetchInvoices(); // Refresh to update payment status
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <FileText className="w-8 h-8 mr-3 text-[#008B8B]" />
            {t('invoices.title')}
          </h1>
          <p className="text-gray-600 mt-1">
            {t('invoices.subtitle')}
          </p>
        </div>

        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-[#008B8B] text-white px-4 py-2 rounded-lg flex items-center hover:bg-[#006666] transition"
        >
          <Plus className="w-5 h-5 mr-2" />
          {t('invoices.createNew')}
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-xl shadow-sm border">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={t('invoices.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full focus:ring-2 focus:ring-[#008B8B] focus:border-[#008B8B]"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="md:w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as InvoiceStatus | 'all')}
              className="w-full py-2 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-[#008B8B]"
            >
              <option value="all">{t('invoices.allStatuses')}</option>
              <option value="draft">{t('invoices.draft')}</option>
              <option value="sent">{t('invoices.sent')}</option>
              <option value="paid">{t('invoices.paid')}</option>
              <option value="overdue">{t('invoices.overdue')}</option>
              <option value="cancelled">{t('invoices.cancelled')}</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={handleSearch}
              className="bg-[#008B8B] text-white px-4 py-2 rounded-lg hover:bg-[#006666] transition"
            >
              <Filter className="w-5 h-5" />
            </button>
            <button 
              onClick={handleExportAll}
              className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition"
              title="Export All Invoices"
            >
              <Download className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#008B8B] mx-auto"></div>
            <p className="text-gray-500 mt-2">{t('common.loading')}</p>
          </div>
        ) : invoices.length === 0 ? (
          <div className="p-8 text-center">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t('invoices.noInvoices')}
            </h3>
            <p className="text-gray-500">
              {t('invoices.noInvoicesDesc')}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('invoices.invoiceNumber')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('invoices.customer')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('invoices.issueDate')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('invoices.dueDate')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('invoices.amount')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('invoices.status')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invoices.map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {invoice.invoice_number}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{invoice.customer?.name || 'N/A'}</div>
                        <div className="text-sm text-gray-500">{invoice.customer?.phone || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(invoice.invoice_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {invoice.due_date ? formatDate(invoice.due_date) : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(invoice.total_amount, invoice.currency)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                          {getStatusIcon(invoice.status)}
                          <span className="ml-1">{t(`invoices.${invoice.status}`)}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handlePreviewInvoice(invoice.id)}
                            className="text-[#008B8B] hover:text-[#006666] p-1"
                            title={t('common.view')}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            className="text-blue-600 hover:text-blue-800 p-1"
                            title={t('common.edit')}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleSendInvoice(invoice)}
                            className="text-green-600 hover:text-green-800 p-1"
                            title={t('invoices.send')}
                          >
                            <Send className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDownloadPDF(invoice.id, invoice.invoice_number)}
                            className="text-gray-600 hover:text-gray-800 p-1"
                            title={t('common.download')}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          {(invoice.status === 'sent' || invoice.status === 'overdue') && (
                            <>
                              <button
                                onClick={() => handleProcessPayment(invoice)}
                                className="text-green-600 hover:text-green-800 p-1"
                                title="Process Payment"
                              >
                                <CreditCard className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleGeneratePaymentLink(invoice)}
                                className="text-blue-600 hover:text-blue-800 p-1"
                                title="Generate Payment Link"
                              >
                                <LinkIcon className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    {t('common.showing')} <span className="font-medium">{(currentPage - 1) * 20 + 1}</span> {t('common.to')} <span className="font-medium">{Math.min(currentPage * 20, invoices.length)}</span> {t('common.of')} <span className="font-medium">{invoices.length}</span> {t('common.results')}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('common.previous')}
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('common.next')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedInvoiceForPayment && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedInvoiceForPayment(null);
          }}
          amount={selectedInvoiceForPayment.total_amount}
          invoiceId={selectedInvoiceForPayment.id}
          customer={{
            id: selectedInvoiceForPayment.customer_id,
            name: selectedInvoiceForPayment.customer?.name || '',
            phone: selectedInvoiceForPayment.customer?.phone || '',
            email: selectedInvoiceForPayment.customer?.email || '',
            country_code: '+965'
          }}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}

      {/* Payment Link Generator Modal */}
      {showLinkGenerator && selectedInvoiceForPayment && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" />
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <PaymentLinkGenerator
                  invoiceId={selectedInvoiceForPayment.id}
                  amount={selectedInvoiceForPayment.total_amount}
                  customer={{
                    id: selectedInvoiceForPayment.customer_id,
                    name: selectedInvoiceForPayment.customer?.name || '',
                    phone: selectedInvoiceForPayment.customer?.phone || '',
                    email: selectedInvoiceForPayment.customer?.email || '',
                    country_code: '+965'
                  }}
                  onLinkGenerated={handlePaymentSuccess}
                />
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={() => {
                      setShowLinkGenerator(false);
                      setSelectedInvoiceForPayment(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Invoice Modal */}
      {showSendModal && selectedInvoiceForSending && (
        <SendInvoiceModal
          isOpen={showSendModal}
          onClose={() => {
            setShowSendModal(false);
            setSelectedInvoiceForSending(null);
          }}
          invoice={selectedInvoiceForSending}
          onSuccess={handleSendSuccess}
        />
      )}

      {/* Process Invoice Payment Modal */}
      {showProcessPaymentModal && selectedInvoiceForProcessing && (
        <ProcessInvoicePaymentModal
          isOpen={showProcessPaymentModal}
          onClose={() => {
            setShowProcessPaymentModal(false);
            setSelectedInvoiceForProcessing(null);
          }}
          invoice={selectedInvoiceForProcessing}
          onSuccess={handleProcessPaymentSuccess}
        />
      )}

      {/* Create Invoice Modal */}
      <CreateInvoiceModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={(invoice) => {
          setShowCreateModal(false);
          fetchInvoices();
          toast.success('Invoice created successfully');
        }}
      />
    </div>
  );
};

export default InvoicesPage;